import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

interface UserProfile {
  email: string;
  role:  string;
}

interface AuthContextType {
  user:     UserProfile | null;
  loading:  boolean;
  signIn:   (email: string, password: string) => Promise<string | null>;
  signOut:  () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user:    null,
  loading: true,
  signIn:  async () => null,
  signOut: async () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user,    setUser]    = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch role from public.users table using the logged-in user email
  async function fetchRole(email: string): Promise<string> {
    const { data } = await supabase
      .from("users")
      .select("role")
      .eq("email", email)
      .single();
    return data?.role || "viewer";
  }

  // On app load — check if already logged in (session persists in browser)
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user?.email) {
        const role = await fetchRole(session.user.email);
        setUser({ email: session.user.email, role });
      }
      setLoading(false);
    });

    // Listen for login/logout events
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user?.email) {
        const role = await fetchRole(session.user.email);
        setUser({ email: session.user.email, role });
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Sign in with email and password
  // Returns null on success, error message on failure
  async function signIn(email: string, password: string): Promise<string | null> {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return error.message;
    return null;
  }

  // Sign out — clears session and returns to login screen
  async function signOut() {
    await supabase.auth.signOut();
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
